import {
  Braces,
  FileCode2,
  Gauge,
  IdCard,
  KeyRound,
  Layers,
  LucideProps,
  Network,
  Plug,
  ServerCog,
  Settings,
  ShieldCheckIcon,
  SlidersHorizontal,
  Tags,
  Users,
} from "lucide-react";
import React, { RefAttributes } from "react";
import type { AgentFeature } from "@/configs/agentFeatures";
import { AuthorityKey } from "@/context/UserContext";
import { ISVG } from "@/icons/interface";
import AiAgentIcon from "@/icons/navigation/ai-agent.svg?react";
import ApplicationIcon from "@/icons/navigation/application.svg?react";
import DomainManagementIcon from "@/icons/navigation/data-domain.svg?react";
import DataSourcesIcon from "@/icons/navigation/datasources.svg?react";
import MCPIcon from "@/icons/navigation/mcp.svg?react";
import ReportsIcon from "@/icons/navigation/reports.svg?react";
import SecurityOverViewIcon from "@/icons/navigation/security-overview.svg?react";
import TrustAIIcon from "@/icons/navigation/trust-ai.svg?react";
import { ConfigParameter } from "@/types/api";
import { AccountTypes } from "./services";

/**
 * LHS groups, in the production order (TRUST-13410): Activity Monitoring first.
 * Group ids predate the rename and are load-bearing (`sidebar-group-*` test ids,
 * `group:` on every entry), so they no longer match their labels:
 *   MonitorInsights -> "Activity Monitoring & Insights"
 *   AISecurity      -> "AI Security"
 *   DataDiscovery   -> "Data Platform Security"
 *   AccessPolicies  -> "Application Security"
 */
export const NavigationGroupOrder = [
  "MonitorInsights",
  "AISecurity",
  "DataDiscovery",
  "AccessPolicies",
  "Administration",
] as const;

export type NavigationGroup = (typeof NavigationGroupOrder)[number];

/**
 * Keyed by every group, so adding one to `NavigationGroupOrder` without a
 * decision on its label is a compile error rather than a raw
 * `sidebar.groups.X` in the UI. `null` renders the group without a header.
 */
export const NavigationGroupTitleKeys: Record<NavigationGroup, string | null> =
  {
    MonitorInsights: "sidebar.groups.MonitorInsights",
    DataDiscovery: "sidebar.groups.DataDiscovery",
    AccessPolicies: "sidebar.groups.AccessPolicies",
    AISecurity: "sidebar.groups.AISecurity",
    Administration: "sidebar.groups.Administration",
  };

export type AdministrationSection =
  | "configuration"
  | "aiPolicyEnforcement"
  | "integrationsAttributes"
  | "usersSecurity"
  | "internalOperations";

export interface INavigation {
  key: string;
  /** Literal label. Omit when `textKey` is set - i18n wins and this would drift. */
  text?: string;
  hideHeaderTitleBar?: boolean;
  description?: string;
  route: string;
  /**
   * Path prefix that marks this entry active, when `route` links deeper than
   * the section it represents. Defaults to `route` up to its first `:param`.
   * Needed when `route` points at a specific tab (to skip a redirect hop) but
   * the entry should stay highlighted on every tab of that page.
   */
  activeBasePath?: string;
  display?: boolean;
  Icon?:
    | React.FC<ISVG>
    | React.ForwardRefExoticComponent<
        Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
      >
    | (React.FC<React.SVGProps<SVGSVGElement>> & {
        title?: string | undefined;
      });
  className?: string;
  autoExpend?: boolean;
  children?: INavigation[];
  newItems?: number;
  whitelist?: string;
  featureConfig?: ConfigParameter;
  /**
   * A part of the new agent experience whose backend may not have shipped:
   * shown (and routable) in demo mode, and in live mode only once the feature
   * is `live` in `@/configs/agentFeatures`. Pair with `featureConfig`.
   */
  agentFeature?: AgentFeature;
  /**
   * Inverse of `featureConfig`: hide this entry while the flag is ON. Used when
   * a preview replaces an existing page rather than adding a new one.
   */
  hiddenByFeatureConfig?: ConfigParameter;
  authorities?: AuthorityKey[];
  accounts?: AccountTypes[];
  isPreview?: boolean;
  onlyForDemo?: boolean;
  notForTrialTenant?: boolean;
  allowedUserTypes?: string[];
  hideWhenNoVisibleChildren?: boolean;
  textKey?: string;
  descriptionKey?: string;
  searchTerms?: string[];
  administrationSection?: AdministrationSection;
  group: NavigationGroup;
}

export const URLs = {
  root: "/",
  welcome: "/app/welcome",
  dashboard: "/app/dashboard",
  unified_lakehouse: "/app/unified_lakehouse",
  unified_lakehouse_type: "/app/unified_lakehouse/:type",
  access_control_policies: "/app/access_control/policies/:id",
  access_control_policies_new: "/app/access_control/policies/:id/new",
  access_control_policies_edit: "/app/access_control/policies/:id/edit",
  domain_policy: "/app/domain_policy",
  privacy_monitoring: "/app/privacy/monitoring",
  privacy_policies: "/app/privacy/policies/:id",
  privacy_policies_new: "/app/privacy/policies/:id/new",
  privacy_policies_edit: "/app/privacy/policies/:id/edit",
  free_trial: "/setup/free_trial",
  aws_singUp: "/setup/:token/aws",
  register_email_verified: "/register/:token/email_verified",
  reset_password: "/reset-password/:token",
  accounts: "/app/accounts",
  aws_data_catalog: "/app/accounts/data_catalog/:id",
  aws_data_sources: "/app/accounts/data_sources/:id",
  account_details: "/app/accounts/:id/details",
  account_details_type: "/app/accounts/:id/details/:type",
  account_details_type_with_children:
    "/app/accounts/:id/details/:type/:children",
  data_catalog: "/app/data_catalog",
  data_classification_admin: "/app/admin/data_classification",
  reports: "/app/reports",
  reports_tab: "/app/reports/:tab",
  user_reports: "/app/users/reports",
  user_reports_type: "/app/users/reports/:type",
  users: "/app/admin/users",
  users_new: "/app/admin/users/new",
  users_attributes: "/app/admin/attribute_management",
  users_attributes_type: "/app/admin/attribute_management/:type",
  legacy_users_attributes: "/app/admin/users_attributes",
  legacy_users_attributes_type: "/app/admin/users_attributes/:type",
  buckets: "/app/admin/buckets",
  group_user_mapping: "/app/admin/group_user_mapping",
  authentication_security: "/app/admin/authentication_security",
  alerts: "/app/alerts",
  reports_detail_audit_report: "/app/reports/details/audit_report",
  reports_detail: "/app/reports/details/:id",
  report_type_url: "/app/reports/:id/:type",
  monitoring: "/app/monitoring/:id",
  monitoring_new: "/app/monitoring/:id/create",
  monitoring_update: "/app/monitoring/:id/update/:policyId",
  report_cis: "/app/reports/:id/:type/cis",
  report_login_activity: "/app/reports/:id/:type/login_activity",
  report_admin_activity: "/app/reports/:id/:type/admin_activity",
  report_user_activity: "/app/reports/:id/:type/user_activity",
  report_user_behaviour_risk_score:
    "/app/reports/:id/:type/user_behaviour_risk_score/:userName?",
  report_data_share_activity: "/app/reports/:id/:type/data_share_activity",
  create_policy: "/app/create_policy/:id",
  view_policy: "/app/view_policy/:id",
  tenant_activation: "/app/admin/tenant_activation",
  oAuth_activation: "/app/oauth/:id",
  applications: "/app/applications",
  resource_policies: "/app/applications/:id/resource_policies",
  resource_policies_type: "/app/applications/:id/resource_policies/:type",
  resource_policy_details_type_with_children:
    "/app/applications/:id/resource_policies/:type/:children",
  ai_agents: "/app/ai_agents",
  ai_agents_resource_policies: "/app/ai_agents/:id/resource_policies",
  ai_agents_resource_policies_type:
    "/app/ai_agents/:id/resource_policies/:type",
  ai_agents_resource_policy_details_type_with_children:
    "/app/ai_agents/:id/resource_policies/:type/:children",
  agent_estate: "/app/agent_estate",
  agent_estate_details: "/app/agent_estate/:id",
  agent_estate_details_type: "/app/agent_estate/:id/:type",
  agent_platforms: "/app/agent_platforms",
  mcp: "/app/mcp",
  mcp_type: "/app/mcp/:type",
  mcp_server_details: "/app/mcp/registry/:id",
  mcp_server_details_type: "/app/mcp/registry/:id/:type",
  administration: "/app/admin",
  administration_data_source_settings: "/app/admin/data_source_settings",
  integrations: "/app/admin/integrations",
  configurations_privilege_template_management:
    "/app/admin/data_source_settings/privilege_template_management",
  configurations_tenant_settings: "/app/admin/tenant_settings",
  configurations_user_defined_functions:
    "/app/admin/data_source_settings/user_defined_functions",
  configurations_user_defined_functions_type:
    "/app/admin/data_source_settings/user_defined_functions/:type",
  configurations_utility_scripts: "/app/admin/utility_scripts",
  configurations_on_prem_metadata: "/app/admin/on_prem_metadata",
  configurations_risk_threshold: "/app/admin/risk_threshold",
  ai_policy_enforcement: "/app/admin/ai_policy_enforcement",
  oauth_client_profiles: "/app/admin/oauth_client_profiles",
  legacy_integrations: "/app/integrations",
  legacy_configurations: "/app/configurations",
  legacy_configurations_privilege_template_management:
    "/app/configurations/privilege_template_management",
  legacy_configurations_tenant_settings: "/app/configurations/tenant_settings",
  legacy_configurations_user_defined_functions:
    "/app/configurations/user_defined_functions",
  legacy_configurations_user_defined_functions_type:
    "/app/configurations/user_defined_functions/:type",
  legacy_configurations_utility_scripts: "/app/configurations/utility_scripts",
  legacy_configurations_risk_threshold: "/app/configurations/risk_threshold",
  legacy_configurations_on_prem_metadata:
    "/app/configurations/on_prem_metadata",

  domain_management: "/app/admin/domain_management",
  domain_management_data_domain: "/app/admin/domain_management/:data_domain_id",
  domain_management_data_domain_type:
    "/app/admin/domain_management/:data_domain_id/:data_domain_type",
  domain_management_data_product:
    "/app/admin/domain_management/:data_domain_id/data-products/:data_product_id",
  domain_management_data_product_type:
    "/app/admin/domain_management/:data_domain_id/data-products/:data_product_id/:data_product_type",
  copilotUI: "/widgets/:id",
  aiLanding: "/app/ai",
  enterprise_policies: "/app/enterprise_policies",
  enterprise_policy_details: "/app/enterprise_policies/:id",
  enterprise_policy_details_type: "/app/enterprise_policies/:id/:type",
  trustai_relationships: "/app/dashboard/relationships",
  trustai_access_flows: "/app/dashboard/access-flows",
};

/**
 * Where an authenticated user lands on `/` or the logo click. A tenant whose
 * `/app/dashboard` is the TrustAI Executive Board (AI Security Governance)
 * lands there, over the AI-mode `/app/ai` landing.
 */
export function resolveDefaultLandingPath({
  hasAccounts,
  isAgentBoardEnabled,
  isAILanding,
}: {
  hasAccounts: boolean;
  /** `isAgentBoardEnabled()` from `@/configs/agentFeatures`. */
  isAgentBoardEnabled: boolean;
  isAILanding: boolean;
}): string {
  if (!hasAccounts) return URLs.welcome;
  if (isAgentBoardEnabled) return URLs.dashboard;
  return isAILanding ? URLs.aiLanding : URLs.dashboard;
}

const ADMINISTRATION_GROUP = "Administration" as const;

/**
 * An Administration capability. `administrationSection` is required here (it is
 * optional on `INavigation`, which also covers entries outside Administration),
 * so a new capability cannot be added without a section to file it under - the
 * Administration page groups strictly by section and would otherwise drop it.
 */
export type AdministrationNavItem = INavigation & {
  administrationSection: AdministrationSection;
};

export const AdministrationNavigationItems: AdministrationNavItem[] = [
  {
    key: "admin_tenant_settings",
    route: URLs.configurations_tenant_settings,
    textKey: "administration.capabilities.tenantSettings.title",
    descriptionKey: "administration.capabilities.tenantSettings.description",
    Icon: SlidersHorizontal,
    authorities: ["TENANT_SETTING:READ"],
    searchTerms: ["tenant", "preferences", "features"],
    administrationSection: "configuration",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_data_source_settings",
    route: URLs.administration_data_source_settings,
    textKey: "administration.capabilities.dataSourceSettings.title",
    descriptionKey:
      "administration.capabilities.dataSourceSettings.description",
    Icon: ServerCog,
    searchTerms: ["data source", "privilege", "template", "udf"],
    administrationSection: "configuration",
    hideWhenNoVisibleChildren: true,
    group: ADMINISTRATION_GROUP,
    children: [
      {
        key: "admin_privilege_template_management",
        route: URLs.configurations_privilege_template_management,
        textKey:
          "administration.capabilities.privilegeTemplateManagement.title",
        descriptionKey:
          "administration.capabilities.privilegeTemplateManagement.description",
        Icon: ShieldCheckIcon,
        authorities: ["PRIVILEGE_TEMPLATE:READ"],
        searchTerms: ["privilege", "template", "access policy"],
        administrationSection: "configuration",
        group: ADMINISTRATION_GROUP,
      },
      {
        key: "admin_user_defined_functions",
        route: URLs.configurations_user_defined_functions,
        textKey: "administration.capabilities.userDefinedFunctions.title",
        descriptionKey:
          "administration.capabilities.userDefinedFunctions.description",
        Icon: Braces,
        authorities: ["USER_DEFINED_FUNCTION:READ"],
        featureConfig: "isUserDefinedFunctionSupported",
        searchTerms: ["udf", "user defined", "function"],
        administrationSection: "configuration",
        group: ADMINISTRATION_GROUP,
      },
    ],
  },
  {
    key: "admin_ai_policy_enforcement",
    route: URLs.ai_policy_enforcement,
    textKey: "administration.capabilities.aiPolicyEnforcement.title",
    descriptionKey:
      "administration.capabilities.aiPolicyEnforcement.description",
    Icon: ShieldCheckIcon,
    authorities: ["ADMINISTRATION:READ"],
    featureConfig: "isNewAgentOnboarding",
    searchTerms: [
      "enforcement",
      "mcp access gateway",
      "mcp gateway",
      "gateway",
      "runtime",
      "policy enforcement",
      "ai policy enforcement",
    ],
    administrationSection: "aiPolicyEnforcement",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_oauth_client_profiles",
    route: URLs.oauth_client_profiles,
    textKey: "administration.capabilities.oauthClientProfiles.title",
    descriptionKey:
      "administration.capabilities.oauthClientProfiles.description",
    Icon: IdCard,
    authorities: ["ADMINISTRATION:READ"],
    featureConfig: "isNewAgentOnboarding",
    agentFeature: "oauthClientProfiles",
    searchTerms: [
      "oauth",
      "oauth client profiles",
      "client profile",
      "client identity",
      "client metadata",
      "cimd",
      "ai policy enforcement",
    ],
    administrationSection: "aiPolicyEnforcement",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_utility_scripts",
    route: URLs.configurations_utility_scripts,
    textKey: "administration.capabilities.utilityScripts.title",
    descriptionKey: "administration.capabilities.utilityScripts.description",
    Icon: FileCode2,
    authorities: ["UTILITY_SCRIPT:ALL"],
    searchTerms: ["utility", "script", "cicd", "pipeline"],
    administrationSection: "configuration",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_risk_threshold",
    route: URLs.configurations_risk_threshold,
    textKey: "administration.capabilities.riskThreshold.title",
    descriptionKey: "administration.capabilities.riskThreshold.description",
    Icon: Gauge,
    authorities: ["TENANT_SETTING:READ"],
    featureConfig: "isRiskThresholdEnabled",
    searchTerms: ["risk", "threshold", "score", "governance"],
    administrationSection: "configuration",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_on_prem_metadata",
    route: URLs.configurations_on_prem_metadata,
    textKey: "administration.capabilities.onPremMetadata.title",
    descriptionKey: "administration.capabilities.onPremMetadata.description",
    Icon: ServerCog,
    authorities: ["GLOBAL_ACCOUNTS:READ"],
    featureConfig: "isOnPremMetadataEnabled",
    searchTerms: ["onprem", "on prem", "metadata", "host", "service user"],
    administrationSection: "configuration",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_integrations",
    route: URLs.integrations,
    textKey: "administration.capabilities.integrations.title",
    descriptionKey: "administration.capabilities.integrations.description",
    Icon: Plug,
    authorities: ["EXTERNAL_INTEGRATION:READ"],
    searchTerms: ["integration", "external"],
    administrationSection: "integrationsAttributes",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_attribute_management",
    route: URLs.users_attributes,
    textKey: "administration.capabilities.attributeManagement.title",
    descriptionKey:
      "administration.capabilities.attributeManagement.description",
    Icon: Tags,
    authorities: ["ENTITLEMENT_ATTRIBUTE:READ"],
    featureConfig: "isManageUserAttributeEnabled",
    searchTerms: ["attribute", "values", "source configuration"],
    administrationSection: "integrationsAttributes",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_users",
    route: URLs.users,
    textKey: "administration.capabilities.userManagement.title",
    descriptionKey: "administration.capabilities.userManagement.description",
    Icon: Users,
    authorities: ["USERS:READ"],
    searchTerms: ["user", "role", "access", "permission"],
    administrationSection: "usersSecurity",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_authentication_security",
    route: URLs.authentication_security,
    textKey: "administration.capabilities.authenticationSecurity.title",
    descriptionKey:
      "administration.capabilities.authenticationSecurity.description",
    Icon: KeyRound,
    authorities: ["ADMINISTRATION:READ"],
    searchTerms: [
      "authentication",
      "attempts",
      "lockout",
      "trustlet",
      "sync user",
      "credentials",
      "password",
    ],
    administrationSection: "usersSecurity",
    group: ADMINISTRATION_GROUP,
  },
  {
    key: "admin_tenant_activation",
    route: URLs.tenant_activation,
    textKey: "administration.capabilities.tenantActivation.title",
    descriptionKey: "administration.capabilities.tenantActivation.description",
    Icon: Settings,
    authorities: ["ADMINISTRATION:READ"],
    whitelist: "isManagementTenant",
    allowedUserTypes: ["TENANT_ROOT_USER"],
    notForTrialTenant: true,
    searchTerms: ["tenant activation", "activate tenant", "feature flags"],
    administrationSection: "internalOperations",
    group: ADMINISTRATION_GROUP,
  },
];

export const Navigations: INavigation[] = [
  {
    key: "dashboard",
    Icon: SecurityOverViewIcon,
    route: URLs.dashboard,
    text: "Security Overview",
    group: "MonitorInsights",
  },
  {
    key: "reports",
    text: "Reports",
    Icon: ReportsIcon,
    route: URLs.reports,
    authorities: ["AUDIT_REPORT:READ", "REPORT:READ", "ACTIVITY_REPORT:READ"],
    group: "MonitorInsights",
  },
  {
    key: "manage_cloud_accounts",
    route: URLs.accounts,
    text: "Data Sources",
    Icon: DataSourcesIcon,
    group: "DataDiscovery",
  },
  {
    key: "unified_lakehouse",
    // Links straight to the landing tab so the nav click skips the page's own
    // redirect hop; `activeBasePath` keeps the entry lit on every other tab.
    route: URLs.unified_lakehouse_type.replace(":type", "catalog"),
    activeBasePath: URLs.unified_lakehouse,
    text: "Unified Lakehouse",
    Icon: Layers,
    featureConfig: "isLakeHouseMetadataEnabled",
    isPreview: false,
    group: "DataDiscovery",
  },
  {
    key: "domain_management",
    route: URLs.domain_management,
    text: "Data Domains",
    Icon: DomainManagementIcon,
    hideHeaderTitleBar: true,
    isPreview: false, // removed preview label
    authorities: ["DATA_DOMAIN:READ"],
    featureConfig: "isDomainProductMappingEnabled",
    group: "DataDiscovery",
  },
  {
    key: "applications",
    route: URLs.applications,
    text: "Applications",
    Icon: ApplicationIcon,
    featureConfig: "isTrustAppEnable",
    isPreview: false,
    authorities: ["APPLICATION:READ"],
    group: "AccessPolicies",
  },
  {
    key: "enterprise_policies",
    route: URLs.enterprise_policies,
    text: "Enterprise Policies",
    Icon: ShieldCheckIcon,
    featureConfig: "isEnterprisePolicyEnabled",
    isPreview: false,
    group: "AccessPolicies",
  },
  {
    key: "mcp_registry",
    route: URLs.mcp,
    text: "MCP Services",
    Icon: MCPIcon,
    group: "AISecurity",
    featureConfig: "isTrustAgentEnable",
  },
  {
    key: "ai_agents",
    route: URLs.ai_agents,
    text: "AI Agents",
    Icon: AiAgentIcon,
    featureConfig: "isTrustAgentEnable",
    hiddenByFeatureConfig: "isNewAgentOnboarding",
    isPreview: false,
    authorities: ["APPLICATION:READ"],
    group: "AISecurity",
  },
  {
    key: "agent_estate",
    route: URLs.agent_estate,
    text: "AI Agents",
    Icon: AiAgentIcon,
    featureConfig: "isNewAgentOnboarding",
    group: "AISecurity",
  },
  {
    key: "copilot_ui",
    Icon: TrustAIIcon,
    route: URLs.aiLanding,
    text: "Guardian Agent",
    featureConfig: "isTrustDSPM",
    isPreview: false,
    authorities: ["TRUSTAI_COPILOT:READ"],
    group: "AISecurity",
  },
  {
    key: "agent_platforms",
    route: URLs.agent_platforms,
    textKey: "agentEstate.agentPlatforms.pageTitle",
    Icon: Network,
    featureConfig: "isNewAgentOnboarding",
    group: "AISecurity",
  },
  {
    key: "administration",
    route: URLs.administration,
    textKey: "administration.title",
    Icon: Settings,
    group: ADMINISTRATION_GROUP,
    hideWhenNoVisibleChildren: true,
    children: AdministrationNavigationItems,
  },
];
